import React, { useCallback, useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import debounce from "lodash.debounce";
import { styled, ThemeProvider } from "@mui/material/styles";
import createTheme from "../../../theme";
import { Box, Button } from "@mui/material";

import {
  INIT_MAP_CONFIG,
  LOCATIONS_LABELS_LAYER,
  LOCATIONS_LAYER,
} from "./config";
import Search from "./Search";
import { useApp } from "../../../AppProvider";
import { createRoot } from "react-dom/client";
import Popup from "../../publicMap/Popup/Popup";

const popupTheme = createTheme();

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const Root = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  gap: 12,
}));

const MapWrapper = styled(Box)({
  position: "relative",
  height: "100%",
});

const MapContainer = styled(Box)({
  height: "100%",
  width: "100%",
  position: "relative",
});

const SelectButton = styled(Button)({
  width: 170,
  position: "absolute",
  bottom: 16,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 10,
});

const Map = ({ data, visibleIds, setWellNdx }) => {
  const { currentUser } = useApp();
  const [map, setMap] = useState();
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const mapContainerRef = useRef(null); // create a reference to the map container
  const [selectedLocation, setSelectedLocation] = useState(null);
  const previousSelectedFeatureId = useRef(null);

  // Filter data based on visibleIds for search
  const filteredData =
    visibleIds && visibleIds.length > 0
      ? data.filter((item) => visibleIds.includes(item.well_ndx))
      : data;

  const handleSelectLocation = useCallback(
    (ndx) => {
      if (!ndx || !map) return;

      if (previousSelectedFeatureId.current !== null) {
        map?.setFeatureState(
          { source: "locations", id: previousSelectedFeatureId.current },
          { selected: false }
        );
      }

      map?.setFeatureState(
        { source: "locations", id: ndx },
        { selected: true }
      );

      previousSelectedFeatureId.current = ndx;

      setSelectedLocation(data.find((loc) => loc.well_ndx === ndx));
    },
    [map, data]
  );

  // Handle Search selection
  const handleSearchSelect = useCallback(
    (location) => {
      if (!location || !map) return;
      handleSelectLocation(location.well_ndx);

      const targetCoords = location.geometry.coordinates;

      const mapHeight = map?.getContainer().clientHeight;

      const pixelOffset = 75;
      const offset = mapHeight / 2 - pixelOffset;

      map?.flyTo({
        center: targetCoords,
        zoom: 16,
        speed: 2.4,
        curve: 0.75,
        offset: [0, offset],
      });
    },
    [handleSelectLocation, map]
  );

  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "100%",
      offset: 15,
      focusAfterOpen: false,
    })
  );
  const popupSizeRef = useRef("small");

  //create map and apply all controls
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      ...INIT_MAP_CONFIG,
    });

    //top left controls
    map.addControl(new mapboxgl.FullscreenControl(), "top-left");
    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "top-left"
    );

    //bottom left controls
    map.addControl(
      new mapboxgl.ScaleControl({ unit: "imperial" }),
      "bottom-left"
    );

    map.on("load", () => {
      setMapIsLoaded(true);
      setMap(map);
    });
  }, []); // eslint-disable-line

  //resizes map when mapContainerRef dimensions changes (sidebar toggle)
  useEffect(() => {
    if (map) {
      const resizer = new ResizeObserver(debounce(() => map?.resize(), 100));
      resizer.observe(mapContainerRef.current);
      return () => {
        resizer.disconnect();
      };
    }
  }, [map]);

  useEffect(() => {
    if (mapIsLoaded && data?.length > 0 && typeof map != "undefined") {
      if (!map?.getSource("locations")) {
        map?.addSource("locations", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: data
              .filter((l) => l?.geometry)
              .map((location) => {
                return {
                  type: "Feature",
                  id: location.well_ndx,
                  properties: {
                    ...location,
                  },
                  geometry: {
                    type: location.geometry.type,
                    coordinates: location.geometry.coordinates,
                  },
                };
              }),
          },
        });

        if (!map?.getLayer("locations")) {
          map?.addLayer(LOCATIONS_LAYER);
          //add labels for locations points
          map?.addLayer(LOCATIONS_LABELS_LAYER);
        }
      } else {
        // Update the source data when data changes
        map?.getSource("locations")?.setData({
          type: "FeatureCollection",
          features: data
            .filter((l) => l?.geometry)
            .map((location) => {
              return {
                type: "Feature",
                id: location.well_ndx,
                properties: {
                  ...location,
                },
                geometry: {
                  type: location.geometry.type,
                  coordinates: location.geometry.coordinates,
                },
              };
            }),
        });
      }

      map?.on("click", "locations", (e) => {
        map?.fire("closeAllPopups");

        if (!e.features.length) return;

        const clickedFeature = e.features[0];
        const clickedFeatureId = clickedFeature?.properties?.well_ndx;

        handleSelectLocation(clickedFeatureId);

        const features = map?.queryRenderedFeatures(e.point);

        const coordinates = [e.lngLat.lng, e.lngLat.lat];

        const popupLayerIds = ["locations"];

        const myFeatures = features.filter((feature) =>
          popupLayerIds.includes(feature?.layer?.id)
        );

        if (myFeatures.length > 0) {
          const popupNode = document.createElement("div");
          const root = createRoot(popupNode);
          root.render(
            <ThemeProvider theme={popupTheme}>
              <Popup
                layers={[LOCATIONS_LAYER]}
                features={myFeatures}
                currentUser={currentUser}
                sizeRef={popupSizeRef}
              />
            </ThemeProvider>
          );

          popUpRef.current
            .setLngLat(coordinates)
            .setDOMContent(popupNode)
            .addTo(map)
            .getElement()
            .querySelector(".mapboxgl-popup-close-button")
            .removeAttribute("aria-hidden");
        }
      });

      map?.on("mouseenter", "locations", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      map?.on("mouseleave", "locations", () => {
        map.getCanvas().style.cursor = "";
      });
    }
  }, [mapIsLoaded, map, data]); //eslint-disable-line

  // Apply filter based on visibleIds
  useEffect(() => {
    if (!map || !mapIsLoaded) return;

    // Check if layers exist before trying to set filter
    if (map?.getLayer("locations") && map?.getLayer("locations-labels")) {
      if (visibleIds && visibleIds.length > 0) {
        // Apply filter to show only visible wells
        const filter = ["in", ["get", "well_ndx"], ["literal", visibleIds]];
        map.setFilter("locations", filter);
        map.setFilter("locations-labels", filter);
      } else {
        // Remove filter to show all wells if no visibleIds provided
        map.setFilter("locations", null);
        map.setFilter("locations-labels", null);
      }
    }
  }, [map, mapIsLoaded, visibleIds]);

  return (
    <Root>
      <Search onSelect={handleSearchSelect} data={filteredData} />

      <MapWrapper component="section">
        <MapContainer ref={mapContainerRef} id="map" tabIndex={-1} />
      </MapWrapper>
      {selectedLocation?.well_ndx && (
        <SelectButton
          variant="contained"
          color="secondary"
          size="large"
          onClick={() => setWellNdx(selectedLocation.well_ndx)}
        >
          Select:&nbsp;<strong>{selectedLocation.well_ndx}</strong>
        </SelectButton>
      )}
    </Root>
  );
};

export default Map;
